import { getApiClient } from './client';
import type { Cohort, CohortMember } from '../types/messaging';

export interface CohortListResponse {
  cohorts: Cohort[];
}

export interface CohortDetailResponse {
  cohort: Cohort;
  members: CohortMember[];
}

export async function listCohorts(tenant: string): Promise<CohortListResponse> {
  const client = getApiClient(tenant);
  return client.get<CohortListResponse>('/cohorts');
}

export async function getCohort(tenant: string, cohortId: string): Promise<CohortDetailResponse> {
  const client = getApiClient(tenant);
  return client.get<CohortDetailResponse>(`/cohorts/${cohortId}`);
}

export async function createCohort(tenant: string, name: string): Promise<{ cohort: Cohort }> {
  const client = getApiClient(tenant);
  return client.post<{ cohort: Cohort }>('/cohorts', { name });
}

export async function updateCohort(
  tenant: string,
  cohortId: string,
  updates: { name?: string }
): Promise<{ cohort: Cohort }> {
  const client = getApiClient(tenant);
  return client.put<{ cohort: Cohort }>(`/cohorts/${cohortId}`, updates);
}

export async function deleteCohort(tenant: string, cohortId: string): Promise<void> {
  const client = getApiClient(tenant);
  await client.delete(`/cohorts/${cohortId}`);
}

export async function addCohortMember(
  tenant: string,
  cohortId: string,
  userId: string,
  role?: 'member' | 'moderator' | 'coach'
): Promise<void> {
  const client = getApiClient(tenant);
  await client.post(`/cohorts/${cohortId}/members`, { userId, role });
}

export async function addCohortMembers(
  tenant: string,
  cohortId: string,
  userIds: string[],
  role?: 'member' | 'moderator' | 'coach'
): Promise<{ count: number }> {
  const client = getApiClient(tenant);
  return client.post(`/cohorts/${cohortId}/members`, { userIds, role });
}

export async function removeCohortMember(
  tenant: string,
  cohortId: string,
  userId: string
): Promise<void> {
  const client = getApiClient(tenant);
  await client.delete(`/cohorts/${cohortId}/members/${userId}`);
}

export interface CohortStats {
  newMessages: number;
  replies: number;
  alerts: number;
}

export interface CohortWithStats {
  cohortId: string;
  cohortName: string;
  avatarUrl: string | null;
  memberCount: number;
  stats: CohortStats;
}

export interface AllCohortStatsResponse {
  stats: CohortWithStats[];
}

export async function getAllCohortStats(tenant: string): Promise<AllCohortStatsResponse> {
  const client = getApiClient(tenant);
  return client.get<AllCohortStatsResponse>('/cohorts/stats/all');
}

export async function markCohortVisited(tenant: string, cohortId: string): Promise<void> {
  const client = getApiClient(tenant);
  await client.post(`/cohorts/${cohortId}/visit`);
}
